'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Bell,
  TrendingUp,
  Activity,
  Thermometer,
  Droplets,
  Wind,
  Sun,
} from 'lucide-react';

const alerts = [
  {
    id: 'temp_high',
    type: 'warning',
    message: 'High temperature detected in Kitchen (24.2°C)',
    timestamp: '2 minutes ago',
    sensor: 'kitchen',
    severity: 'medium',
  },
  {
    id: 'humidity_low',
    type: 'info',
    message: 'Low humidity in Server Room (35.2%)',
    timestamp: '5 minutes ago',
    sensor: 'server_room',
    severity: 'low',
  },
  {
    id: 'motion_detected',
    type: 'success',
    message: 'Motion detected in Produce Section',
    timestamp: '1 minute ago',
    sensor: 'produce',
    severity: 'low',
  },
];

interface DetailsSidebarProps {
  selectedSensor: any;
}

export default function DetailsSidebar({ selectedSensor }: DetailsSidebarProps) {
  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return <Bell className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <div className="w-80 bg-background border-l border-border flex flex-col shadow-sm">
      <Tabs defaultValue="alerts" className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-2 m-4">
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>

        <TabsContent value="alerts" className="flex-1 overflow-auto p-4 pt-0">
          <div className="space-y-3">
            {alerts.map((alert) => (
              <Alert key={alert.id} className="border-l-4 border-l-orange-500">
                <div className="flex items-start gap-2">
                  {getAlertIcon(alert.type)}
                  <div className="flex-1">
                    <AlertDescription className="text-sm">{alert.message}</AlertDescription>
                    <div className="text-xs text-muted-foreground mt-1">{alert.timestamp}</div>
                  </div>
                  <Badge variant={alert.severity === 'high' ? 'destructive' : 'secondary'}>{alert.severity}</Badge>
                </div>
              </Alert>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="details" className="flex-1 overflow-auto p-4 pt-0">
          {selectedSensor ? (
            <div className="space-y-4">
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{selectedSensor.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                      <Thermometer className="w-6 h-6 text-red-500 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-red-600">{selectedSensor.temperature}°C</div>
                      <div className="text-xs text-muted-foreground">Temperature</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <Droplets className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-blue-600">{selectedSensor.humidity}%</div>
                      <div className="text-xs text-muted-foreground">Humidity</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                      <Wind className="w-6 h-6 text-green-500 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-green-600">{selectedSensor.airQuality}</div>
                      <div className="text-xs text-muted-foreground">Air Quality</div>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <Sun className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-yellow-600">{selectedSensor.light}</div>
                      <div className="text-xs text-muted-foreground">Light (lux)</div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge
                        variant="default"
                        className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                      >
                        Online
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Last Update:</span>
                      <span>2 minutes ago</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Motion:</span>
                      <Badge variant={selectedSensor.motion ? 'default' : 'secondary'}>
                        {selectedSensor.motion ? 'Detected' : 'None'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Recent Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <TrendingUp className="w-4 h-4 text-green-500" />
                      <span>Temperature stable</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <TrendingUp className="w-4 h-4 text-blue-500" />
                      <span>Humidity increasing</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Activity className="w-4 h-4 text-green-500" />
                      <span>Air quality good</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center text-muted-foreground mt-8">
              <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select a sensor to view details</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
